import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';

import { StarRating } from '../Ratings';
import { BookingForm } from '../BookingForm';
import { FollowButton } from './FollowButton';
import {
  UpdatesPanel,
  useListingUpdates,
  OverviewPanel,
  GalleryPanel,
  FacilitiesPanel,
  PricingPanel,
  PoliciesPanel,
  ReviewsPanel,
  LocationPanel,
  visibleSections,
} from './panels';

import { listingMedia } from '../../../data/listingAttributes';
import { getProvider, isVerifiedOperator } from '../../../utils/providers';
import { formatUnitPrice } from '../../../utils/pricing';
import { useApp } from '../../../context';
import { isListingBooked } from '../../../utils/bookedListings';
import './styles.css';

/** Initials for the operator's mark. Never blank, never an empty circle. */
function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

/**
 * One listing, rendered in full.
 *
 * Rebuilt from the three-tab page (About / Map / Reviews) into the sectioned
 * profile the design calls for: a masthead carrying identity and price, a
 * photo mosaic, then one tab per section that has something in it.
 *
 * WHAT CHANGED BENEATH IT
 * -----------------------
 * Every section is now provider data. The old page rendered a HARDCODED list
 * of eight amenities on every listing — a national park advertising a bar —
 * because there was no field to enter them into. Facilities, policies and
 * seasonal rates did not exist at all. See data/listingAttributes.js, which is
 * mirrored with the provider app.
 *
 * TABS ARE CONDITIONAL. `visibleSections` offers a tab only when its panel has
 * content, so this page never renders "No facilities information currently
 * available" — an empty tab reads as a broken page, a missing one reads as a
 * listing that has not filled that part in.
 *
 * `ListingDetailDialog` (./dialog) keeps the modal presentation for the one
 * place it is still right — previewing an option from inside the itinerary
 * builder, where routing away would drop the in-progress selection.
 *
 * @param listing   a listings document
 * @param onBack    what the Back button does — navigate, or close the dialog
 * @param inDialog  pins the grid to the viewport and lets it scroll itself.
 *                  As a page the document scrolls instead, so a nested 100vh
 *                  scroller here would produce two scrollbars.
 */
export function ListingDetail({ listing, onBack, inDialog = false }) {
  const [openBooking, setOpen] = React.useState(false);
  const { bookings } = useApp();

  const { updates, unseen, markSeen } = useListingUpdates(listing?.id);

  const sections = React.useMemo(
    () => visibleSections(listing, { hasUpdates: updates.length > 0 }),
    [listing, updates.length]
  );
  const [section, setSection] = React.useState('overview');

  /**
   * Opening Updates marks them read.
   *
   * Done on the change rather than in the panel's own effect, so a listing
   * that lands on Updates because it is the first tab does not clear the dot
   * before the traveller has looked at anything.
   */
  const openSection = (next) => {
    setSection(next);
    if (next === 'updates') markSeen();
  };

  // A listing whose gallery is removed while its tab is open would otherwise
  // leave the page showing a section that no longer exists.
  React.useEffect(() => {
    if (!sections.some((s) => s.key === section)) setSection('overview');
  }, [sections, section]);

  /**
   * The badge comes from the OPERATOR's KYC, read from `service_providers`.
   *
   * Never from the listing: that document is written by the provider, and a
   * verification flag they can set themselves is not a verification. Same
   * reasoning that keeps `ratingAverage` out of their hands in the rules.
   */
  const [verified, setVerified] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    const providerId = listing?.company?.id || listing?.serviceProviderId;
    if (!providerId) { setVerified(false); return undefined; }

    getProvider(providerId).then((provider) => {
      if (live) setVerified(isVerifiedOperator(provider));
    });

    return () => { live = false; };
  }, [listing?.company?.id, listing?.serviceProviderId]);

  const media = React.useMemo(() => listingMedia(listing), [listing]);
  const hero = media.slice(0, 5);

  /**
   * Derived here rather than read off `listing.booked`.
   *
   * That flag is set by IndexPage, which merges bookings into the listings it
   * renders — so it exists only on a listing reached from the home feed. A
   * listing opened directly by URL never passes through that merge, and the
   * button would have offered to book something already booked.
   */
  const booked = React.useMemo(
    () => isListingBooked(bookings, listing?.id),
    [bookings, listing?.id]
  );

  return (
    <Box className={`provider-grid${inDialog ? ' provider-grid--dialog' : ''}`}>
      {/* `minWidth: 0` because this is a GRID ITEM, and grid items default to
          min-width:auto — so a long address or title sizes the column to its
          own content and pushes past a narrow viewport instead of wrapping. */}
      <Box sx={{ width: '100%', minWidth: 0, mt: inDialog ? 7.5 : 0 }}>
        {/* ── Masthead ─────────────────────────────────── */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
          <Button onClick={onBack} variant="text" startIcon={<ChevronLeftIcon />} sx={{ mb: 1, px: 0 }}>
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Avatar
              variant="rounded"
              sx={{
                width: 56,
                height: 56,
                fontWeight: 800,
                color: 'primary.main',
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16),
              }}
            >
              {initialsOf(listing?.company?.name || listing?.name)}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {listing?.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 0.75 }}>
                <StarRating listing={listing} size="small" />
                {listing?.company?.name && (
                  <Typography variant="body2" color="text.secondary">
                    by {listing.company.name}
                  </Typography>
                )}
                {verified && (
                  <Tooltip title="This operator has completed Vhakacha's verification checks">
                    <Chip
                      size="small"
                      icon={<VerifiedRoundedIcon />}
                      label="Verified operator"
                      variant="outlined"
                      color="success"
                    />
                  </Tooltip>
                )}
                {/* Follows the OPERATOR, so it sits with the operator's name
                    rather than with the booking controls. */}
                <FollowButton provider={listing?.company} />
              </Box>

              {/* `minWidth: 0` on the row, or a long address cannot shrink
                  below its content width and runs off a 375px screen. */}
              {(listing?.address || listing?.location) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75,
                    mt: 1,
                    minWidth: 0,
                    color: 'text.secondary',
                  }}
                >
                  <LocationOnRoundedIcon sx={{ fontSize: '1.05rem', flexShrink: 0, mt: 0.25 }} />
                  <Typography variant="body2" sx={{ minWidth: 0 }}>
                    {[listing.address, listing.location].filter(Boolean).join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Price and the booking CTA together — the figure a traveller is
              charged sits with the button that charges it, never beside a
              published seasonal rate that does not. */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            {listing?.cost != null && (
              <Typography variant="h6" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                {formatUnitPrice(listing.cost, listing.currency, listing.priceUnit)}
              </Typography>
            )}
            <Button
              onClick={() => setOpen(true)}
              variant="contained"
              startIcon={<CalendarMonthOutlinedIcon />}
              disabled={booked}
            >
              {booked ? 'Booked!' : 'Make A Booking'}
            </Button>
          </Box>
        </Box>

        {/* ── Photo mosaic ─────────────────────────────── */}
        {hero.length > 0 && (
          <Box sx={{ px: { xs: 2, sm: 3 }, mt: 2.5 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                borderRadius: 3,
                overflow: 'hidden',
                gridTemplateColumns: { xs: '1fr', sm: '1.6fr 1fr 1fr' },
                gridTemplateRows: { xs: '220px', sm: '150px 150px' },
                '& > :first-of-type': { gridRow: { sm: 'span 2' } },
                // One photo on a phone: four thumbnails at a third of a
                // 375px screen are decoration, not a gallery. The Gallery tab
                // is where the rest live.
                '& > :not(:first-of-type)': { display: { xs: 'none', sm: 'block' } },
              }}
            >
              {hero.map((photo, i) => (
                <Box
                  key={`${photo.url}-${i}`}
                  component="img"
                  src={photo.url}
                  alt={photo.caption || `${listing?.name || 'Listing'} photo ${i + 1}`}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    backgroundColor: 'action.hover',
                  }}
                />
              ))}
            </Box>

            {media.length > hero.length && sections.some((s) => s.key === 'gallery') && (
              <Button
                onClick={() => setSection('gallery')}
                variant="outlined"
                size="small"
                startIcon={<PhotoLibraryOutlinedIcon />}
                sx={{ mt: 1.5, borderRadius: 999 }}
              >
                Show all {media.length} photos
              </Button>
            )}
          </Box>
        )}

        {/* ── Sections ─────────────────────────────────── */}
        <Box sx={{ mt: 3, borderBottom: 1, borderColor: 'divider', px: { xs: 1, sm: 2 } }}>
          <Tabs
            value={section}
            onChange={(event, next) => openSection(next)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Listing sections"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: 'text.secondary',
                minHeight: 44,
              },
              '& .Mui-selected': { color: 'primary.main' },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                backgroundColor: 'primary.main',
              },
            }}
          >
            {sections.map((s) => (
              <Tab
                key={s.key}
                value={s.key}
                label={
                  s.key === 'updates' && unseen ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      {s.label}
                      {/* Labelled, not decorative: a coloured dot is invisible
                          to a screen reader and to anyone who cannot pick it
                          out against the tab text. */}
                      <Box
                        component="span"
                        aria-label="New updates"
                        sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'secondary.main' }}
                      />
                    </Box>
                  ) : (
                    s.label
                  )
                }
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
          {section === 'updates' && <UpdatesPanel listing={listing} updates={updates} />}
          {section === 'overview' && <OverviewPanel listing={listing} />}
          {section === 'gallery' && <GalleryPanel listing={listing} />}
          {section === 'facilities' && <FacilitiesPanel listing={listing} />}
          {section === 'pricing' && (
            <PricingPanel listing={listing} booked={booked} onReserve={() => setOpen(true)} />
          )}
          {section === 'policies' && <PoliciesPanel listing={listing} />}
          {section === 'reviews' && <ReviewsPanel listing={listing} />}
          {section === 'location' && <LocationPanel listing={listing} />}
        </Box>
      </Box>

      <BookingForm open={openBooking} setOpen={setOpen} serviceProvider={listing} />
    </Box>
  );
}

export default ListingDetail;
